import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import sellerApi from "@/lib/sellerApi";
import { formatApiError } from "@/lib/api";
import { rupee } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2, MapPin, Phone, Truck, X } from "lucide-react";

const TABS = ["NEW", "ACCEPTED", "PREPARING", "READY", "COMPLETED", "CANCELLED"];
const NEXT = { NEW: "ACCEPTED", ACCEPTED: "PREPARING", PREPARING: "READY", READY: "COMPLETED" };
const NEXT_LABEL = { NEW: "Accept", ACCEPTED: "Start Preparing", PREPARING: "Mark Ready", READY: "Mark Completed" };
const STATUS_STYLES = {
  NEW: "bg-blue-100 text-blue-700", ACCEPTED: "bg-amber-100 text-amber-700", PREPARING: "bg-purple-100 text-purple-700",
  READY: "bg-teal-100 text-teal-700", COMPLETED: "bg-sage/15 text-sage", CANCELLED: "bg-red-100 text-red-700",
};

export default function SellerOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("NEW");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await sellerApi.get(`/seller/orders?status=${tab}`);
      setOrders(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (o, status) => {
    let reason = "";
    if (status === "CANCELLED") reason = window.prompt("Reason for cancellation:", "") || "";
    setBusy(true);
    try {
      await sellerApi.put(`/seller/orders/${o.id}/status`, { status, reason });
      toast.success(`Order ${status.toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="seller-orders">
      <div>
        <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Orders</h1>
        <p className="text-ink-soft">Accept and process orders for your shop.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`order-tab-${t.toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === t ? "bg-brand text-ink" : "border border-border text-ink-soft hover:border-brand"}`}>{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center">
          <ShoppingCart className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-3 font-heading text-lg text-ink">No {tab.toLowerCase()} orders</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`seller-order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-bold text-ink">#{o.seller_order_number}</p>
                  <p className="text-xs text-ink-muted">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[o.status]}`}>{o.status}</span>
              </div>

              <div className="mt-3 space-y-1 text-sm">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between text-ink-soft">
                    <span>{it.name} × {it.quantity}</span>
                    <span>{rupee(it.line_total)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-border pt-2 font-semibold text-ink">
                  <span>Seller Amount</span><span>{rupee(o.seller_amount)}</span>
                </div>
              </div>

              <div className="mt-3 space-y-1 text-sm text-ink-soft">
                <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {o.customer_name} · {o.customer_mobile}</p>
                <p className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {o.address}{o.landmark ? `, ${o.landmark}` : ""}</p>
                <p className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> {o.fulfillment} · Payment: COD</p>
                {o.cancel_reason && <p className="text-destructive">Cancelled: {o.cancel_reason}</p>}
              </div>

              {o.status !== "COMPLETED" && o.status !== "CANCELLED" && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {NEXT[o.status] && (
                    <Button disabled={busy} className="rounded-full bg-navy text-white hover:bg-navy-hover" onClick={() => changeStatus(o, NEXT[o.status])} data-testid={`order-advance-${o.id}`}>
                      {NEXT_LABEL[o.status]}
                    </Button>
                  )}
                  <Button disabled={busy} variant="outline" className="rounded-full text-destructive" onClick={() => changeStatus(o, "CANCELLED")} data-testid={`order-cancel-${o.id}`}>
                    <X className="mr-1 h-4 w-4" /> Cancel
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
